import { useQuery } from "@tanstack/react-query";
import { Handle, Position } from "@xyflow/react";
import clsx from "clsx";
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { tv } from "tailwind-variants";

import { Button } from "@/components/base/button";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { Link } from "@/components/base/link";
import { OverrideScoreForm } from "@/components/matches/director-controls/override-score";
import { RefsList } from "@/components/refs/refs-list";
import { TeamNames } from "@/components/teams/names";
import {
  useActiveDivisionId,
  useCanUpdateTournament,
  useIsDemoTournament,
  usePlayoffsQueryOptions,
  useTournament,
} from "@/components/tournaments/context";
import { ForfeitMatchMenu } from "@/components/tournaments/controls/forfeit-match";
import { SetCourtForm } from "@/components/tournaments/controls/set-court";
import { SimulateMatchModal } from "@/components/tournaments/controls/simulate-match";
import { SwitchTeamForm } from "@/components/tournaments/controls/switch-team";
import { LiveRefetchButton } from "@/components/tournaments/panels/games/pool-match-grid";
import { getViewerProfilesQueryOptions } from "@/functions/profiles/get-viewer-profiles";
import { useTRPC } from "@/integrations/trpc/react";
import { formatScheduledTime } from "@/lib/dates";
import { getSetScore, hasInProgressSet, isMatchLive } from "@/lib/matches";
import { formatOrdinals } from "@/lib/numbers";
import { isDefined, isNotNullOrUndefined } from "@/utils/types";

import type { BracketMatch } from ".";
import {
  useActiveTeam,
  useBracketTeamMap,
  usePoolNames,
  useSeedMap,
  useSetActiveTeam,
  useSetNodeIdToCenter,
} from ".";
import { UpdateScoreOptionsForm } from "./update-score-options";
import { Wildcard } from "./wildcard";

export const scoreTextStyles = tv({
  base: "p-3 text-center flex flex-col justify-center col-span-1 text-xl font-light border-gray-300",
  variants: {
    winner: {
      true: "",
      false: "",
    },
    inProgress: {
      true: "font-bold",
    },
    last: {
      true: ["border-r-0"],
      false: ["border-r"],
    },
  },
  compoundVariants: [
    {
      winner: false,
      inProgress: false,
      class: "text-gray-400",
    },
  ],
});

const headerStyles = tv({
  base: "grid grid-cols-6 items-center border-b border-gray-300",
  variants: {
    viewerRole: {
      reffing: "bg-red-700 text-white",
      playing: "bg-blue-200 text-blue-900",
      none: "",
    },
  },
  defaultVariants: {
    viewerRole: "none",
  },
});

export function MatchNode({
  data,
}: {
  data: BracketMatch & {
    roundIdx: number;
  };
  type: string;
}) {
  const tournament = useTournament();
  const isDemo = useIsDemoTournament();

  const [overrideId, setOverrideId] = useState<number | undefined>(undefined);
  const [scoreOptionsOpen, setScoreOptionsOpen] = useState(false);
  const [switchSlot, setSwitchSlot] = useState<
    { slot: "teamAId" | "teamBId"; currentTeamId: number | null } | undefined
  >(undefined);
  const divisionId = useActiveDivisionId();

  const {
    court,
    sets,
    winnerId,
    roundIdx,
    matchNumber,
    status,
    teamAPreviousMatchId,
    teamBPreviousMatchId,
    tournamentDivisionId,
    refs,
    loserFinish,
  } = data;

  const teamMap = useBracketTeamMap();
  const teamA = data.teamAId ? teamMap.get(data.teamAId) : undefined;
  const teamB = data.teamBId ? teamMap.get(data.teamBId) : undefined;

  const trpc = useTRPC();
  const activeTeam = useActiveTeam();
  const setActiveTeam = useSetActiveTeam();
  const playoffsOptions = usePlayoffsQueryOptions();
  const { refetch } = useQuery(playoffsOptions);

  const matchIsLive = isMatchLive({ status: data.status, sets });

  const { data: scoreEventsMap } = useQuery({
    ...trpc.matches.getScoreEvents.queryOptions({ playoffMatchId: data.id }),
    enabled: hasInProgressSet({ status: data.status, sets }),
  });

  const { data: viewerProfiles } = useQuery(getViewerProfilesQueryOptions());
  const viewerProfileIds = useMemo(
    () => new Set(viewerProfiles?.map((p) => p.id)),
    [viewerProfiles],
  );
  const viewerRole = useMemo(() => {
    if (refs.some((r) => viewerProfileIds.has(r.profile.id))) return "reffing";
    const allPlayers = [
      ...(teamA?.team.players ?? []),
      ...(teamB?.team.players ?? []),
    ];
    if (allPlayers.some((p) => viewerProfileIds.has(p.profile.id)))
      return "playing";
    return "none";
  }, [refs, teamA, teamB, viewerProfileIds]);

  const seedMap = useSeedMap();
  const poolNames = usePoolNames();

  const { data: teamAPreviousMatch } = useQuery({
    ...playoffsOptions,
    select: (data) => data.find(({ id }) => id === teamAPreviousMatchId),
  });

  const teamAPreviousMatchName = teamAPreviousMatch ? (
    <>
      {teamAPreviousMatch.sets.length > 1 ? "Match" : "Game"}{" "}
      {teamAPreviousMatch.matchNumber}
    </>
  ) : null;

  const { data: teamBPreviousMatch } = useQuery({
    ...playoffsOptions,
    select: (data) => data.find(({ id }) => id === teamBPreviousMatchId),
  });

  const teamBPreviousMatchName = teamBPreviousMatch ? (
    <>
      {teamBPreviousMatch.sets.length > 1 ? "Match" : "Game"}{" "}
      {teamBPreviousMatch.matchNumber}
    </>
  ) : null;

  const setNodeIdToCenter = useSetNodeIdToCenter();

  const canUpdate = useCanUpdateTournament();

  const name = `${sets.length > 1 ? "Match" : "Game"} ${matchNumber}`;

  const formattedScheduledTime = data.scheduledTime
    ? formatScheduledTime(data.scheduledTime)
    : null;

  return (
    <div>
      <div className="flex flex-row items-center space-x-2 p-3">
        <RefsList
          tournamentDivisionId={tournamentDivisionId}
          playoffMatchId={data.id}
          matchStatus={
            isDefined(data.teamAId) && isDefined(data.teamBId)
              ? data.status
              : "tbd"
          }
          refs={refs}
          isDemo={isDemo}
        />
        {viewerRole === "reffing" && data.status !== "completed" && (
          <Link
            to="/matches/playoff/$matchId"
            params={{ matchId: data.id.toString() }}
            className="flex items-center gap-1 rounded-sm bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200"
          >
            Score <ArrowRightIcon size={14} />
          </Link>
        )}
      </div>
      <div
        className="relative w-md overflow-hidden rounded-md border border-gray-300 bg-white"
        data-id={data.id}
      >
        <div className={headerStyles({ viewerRole })}>
          <div
            className={clsx(
              "p-3 flex flex-row flex-wrap items-center gap-3",
              sets.length > 1 ? "col-span-3" : "col-span-5",
            )}
          >
            <Link
              to="/matches/playoff/$matchId"
              params={{
                matchId: data.id.toString(),
              }}
              variant="alt"
              className={clsx(
                "flex flex-row items-center gap-4 rounded-sm px-2 py-1 font-medium underline",
                viewerRole !== "none"
                  ? "bg-white/20 hover:bg-white/30"
                  : "hover:bg-gray-100",
              )}
            >
              <span className="text-ellipsis whitespace-nowrap">
                {matchIsLive && viewerRole === "reffing"
                  ? `Tap to score ${name}`
                  : name}
              </span>
            </Link>
            {data.forfeitTeamId && (
              <span
                className={clsx(
                  "text-xs font-medium",
                  viewerRole === "reffing" ? "text-red-200" : "text-red-500",
                )}
              >
                Forfeit
              </span>
            )}
            {formattedScheduledTime && (
              <span
                className={clsx(
                  "text-xs",
                  viewerRole !== "none" ? "opacity-70" : "text-gray-500",
                )}
              >
                {formattedScheduledTime}
              </span>
            )}
            {canUpdate &&
              isDemo &&
              teamA &&
              teamB &&
              data.status !== "completed" && (
                <SimulateMatchModal
                  tournamentDivisionId={tournamentDivisionId}
                  playoffMatchId={data.id}
                />
              )}
            {canUpdate && teamA && teamB && (
              <ForfeitMatchMenu
                playoffMatchId={data.id}
                teamA={teamA}
                teamB={teamB}
                status={data.status}
              />
            )}

            {tournament && (
              <SetCourtForm
                tournamentId={tournament?.id}
                tournamentDivisionId={tournamentDivisionId}
                playoffMatchId={data.id}
                name={name}
                court={court}
                completed={status === "completed"}
              />
            )}

            {matchIsLive ? <LiveRefetchButton refetch={refetch} /> : null}
          </div>

          {sets.map((s) => (
            <div
              key={s.id}
              className="col-span-1 flex flex-row items-center justify-center gap-2 p-3 text-center whitespace-nowrap"
            >
              <span>{sets.length > 1 ? <>Set {s.setNumber}</> : "Score"}</span>
              {canUpdate && (
                <DropdownMenu
                  label="Score actions"
                  tooltip="Score actions"
                  buttonVariant="text"
                  buttonSize="xs"
                  buttonClassName="self-end text-blue-500 hover:text-blue-600"
                  buttonIcon={<EditIcon size={16} />}
                >
                  <DropdownMenuItem onAction={() => setOverrideId(s.id)}>
                    Override score
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    isDisabled={!!winnerId}
                    onAction={() => setScoreOptionsOpen(true)}
                  >
                    Update score options
                  </DropdownMenuItem>
                </DropdownMenu>
              )}
              {canUpdate && overrideId === s.id && (
                <OverrideScoreForm
                  tournamentDivisionId={data.tournamentDivisionId}
                  playoffMatchId={data.id}
                  setId={s.id}
                  isOpen={true}
                  onOpenChange={(open) => {
                    if (!open) setOverrideId(undefined);
                  }}
                />
              )}
            </div>
          ))}
          {scoreOptionsOpen && (
            <UpdateScoreOptionsForm
              playoffMatchId={data.id}
              matchName={name}
              currentSets={sets.map((s) => ({
                winScore: s.winScore,
                switchScore: s.switchScore,
              }))}
              isOpen={true}
              onOpenChange={setScoreOptionsOpen}
            />
          )}
        </div>
        {[
          isNotNullOrUndefined(teamA)
            ? {
                ...teamA,
                previousMatchId: data.teamAPreviousMatchId,
                type: "MatchTeam" as const,
              }
            : {
                id: (data.teamAPreviousMatchId ??
                  data.teamAPoolId ??
                  ("a" as const)) as number | "a" | "b",
                type: "tbd" as const,
              },
          isNotNullOrUndefined(teamB)
            ? {
                ...teamB,
                previousMatchId: data.teamBPreviousMatchId,
                type: "MatchTeam" as const,
              }
            : {
                id: (data.teamBPreviousMatchId ??
                  data.teamBPoolId ??
                  ("b" as const)) as number | "a" | "b",
                type: "tbd" as const,
              },
        ].map((team, i) => {
          const slotSeed = i === 0 ? data.teamASeed : data.teamBSeed;
          const mapping =
            slotSeed != null && seedMap ? seedMap[slotSeed - 1] : undefined;
          const seedPoolName =
            mapping && poolNames ? poolNames[mapping.pool] : undefined;
          const opp = i === 0 ? teamB : teamA;
          const wildcardOpponent = opp?.poolTeam?.finish
            ? { finish: opp.poolTeam.finish, poolName: opp.poolTeam.pool.name }
            : null;

          const previousMatchId =
            i === 0 ? teamAPreviousMatchId : teamBPreviousMatchId;

          return (
            <div
              key={team.type === "tbd" ? (i === 0 ? "a" : "b") : team.id}
              className={clsx(
                "relative grid grid-cols-6 items-stretch border-b border-gray-300 last-of-type:border-b-0",
                team.id === activeTeam ? "bg-gray-200" : "bg-white",
                data.forfeitTeamId === team.id && "opacity-50",
              )}
              role="none"
              tabIndex={-1}
              onMouseEnter={() => {
                if (typeof team.id === "number") {
                  setActiveTeam(team.id as number);
                } else {
                  setActiveTeam(null);
                }
              }}
              onMouseLeave={() => {
                setActiveTeam(null);
              }}
            >
              {previousMatchId != null && (
                <Button
                  variant="icon"
                  className="absolute top-1/2 -left-10 z-10 hidden -translate-y-1/2 rounded-full border border-gray-300 bg-white p-1.5 shadow-sm transition-colors hover:bg-gray-100 md:flex"
                  onPress={() => setNodeIdToCenter(previousMatchId)}
                >
                  <ChevronLeftIcon size={18} className="text-gray-500" />
                </Button>
              )}
              {i === 0 && data.nextMatchId != null && (
                <Button
                  variant="icon"
                  className="absolute -right-10 bottom-0 z-10 hidden translate-y-1/2 rounded-full border border-gray-300 bg-white p-1.5 shadow-sm transition-colors hover:bg-gray-100 md:flex"
                  onPress={() => {
                    if (data.nextMatchId != null) {
                      setNodeIdToCenter(data.nextMatchId);
                    }
                  }}
                >
                  <ChevronRightIcon size={18} className="text-gray-500" />
                </Button>
              )}
              <Handle
                type="target"
                position={Position.Left}
                id={team.id.toString()}
                style={{
                  opacity: 0,
                  position: "absolute",
                  top: i === 0 ? "45%" : "78%",
                  right: 0,
                }}
              />
              <div
                className={clsx(
                  "p-3 grid grid-cols-10 space-x-2 items-center",
                  sets.length > 1 ? "col-span-3" : "col-span-5",
                )}
              >
                {team.type === "MatchTeam" && (
                  <span
                    className={clsx(
                      "text-center",
                      team.playoffsSeed || team.wildcard
                        ? "col-span-1"
                        : "hidden",
                    )}
                    title={`Seed ${team.playoffsSeed ?? "?"} in playoffs`}
                  >
                    {team.playoffsSeed ?? (team.wildcard ? "WC" : null)}
                  </span>
                )}

                <div
                  className={clsx(
                    "flex flex-col col-span-9",
                    winnerId && winnerId === team.id && "font-bold",
                    winnerId && winnerId !== team.id && "text-gray-400",
                  )}
                >
                  {team.type === "MatchTeam" ? (
                    <div className="flex flex-row items-center gap-2">
                      <TeamNames
                        players={team?.team.players}
                        link={false}
                        orientation="col"
                      />
                      {data.forfeitTeamId === team.id && (
                        <span className="text-xs font-medium text-red-500">
                          FF
                        </span>
                      )}
                      {canUpdate && status !== "completed" && (
                        <Button
                          variant="icon"
                          className="shrink-0"
                          onPress={() =>
                            setSwitchSlot({
                              slot: i === 0 ? "teamAId" : "teamBId",
                              currentTeamId: team.id as number,
                            })
                          }
                          tooltip="Switch team"
                        >
                          <EditIcon
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </Button>
                      )}
                    </div>
                  ) : mapping && seedPoolName ? (
                    <div className="flex flex-row items-center gap-1">
                      <span className="text-sm text-gray-500 italic">
                        {formatOrdinals(mapping.seed)} in Pool{" "}
                        <span className="uppercase">{seedPoolName}</span>
                      </span>
                      {canUpdate && status !== "completed" && (
                        <Button
                          variant="icon"
                          className="shrink-0"
                          onPress={() =>
                            setSwitchSlot({
                              slot: i === 0 ? "teamAId" : "teamBId",
                              currentTeamId: null,
                            })
                          }
                          tooltip="Assign team"
                        >
                          <EditIcon
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </Button>
                      )}
                    </div>
                  ) : roundIdx === 0 ? (
                    <Wildcard
                      matchId={data.id}
                      a={team.id === "a"}
                      opponent={wildcardOpponent}
                    />
                  ) : (
                    <div className="flex flex-row items-center gap-1">
                      <Button
                        className="self-start px-0 hover:underline"
                        variant="text"
                        onPress={() => {
                          if (typeof team.id === "number") {
                            setNodeIdToCenter(team.id as number);
                          }
                        }}
                      >
                        Winner of{" "}
                        {team.id === teamAPreviousMatchId
                          ? teamAPreviousMatchName
                          : teamBPreviousMatchName}
                      </Button>
                      {canUpdate && status !== "completed" && (
                        <Button
                          variant="icon"
                          className="shrink-0"
                          onPress={() =>
                            setSwitchSlot({
                              slot: i === 0 ? "teamAId" : "teamBId",
                              currentTeamId: null,
                            })
                          }
                          tooltip="Assign team"
                        >
                          <EditIcon
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </Button>
                      )}
                    </div>
                  )}
                  {team.type === "MatchTeam" && team.poolTeam?.finish && (
                    <div>
                      <span className="text-xs text-gray-500">
                        {formatOrdinals(team.poolTeam.finish)} in Pool{" "}
                        <span className="uppercase">
                          {team.poolTeam.pool.name}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {sets
                .sort((a, b) => a.setNumber - b.setNumber)
                .map((s, i) => {
                  const score = getSetScore({
                    ...s,
                    scoreEvents: scoreEventsMap?.[s.id],
                  });
                  return (
                    <div
                      key={s.id}
                      className={scoreTextStyles({
                        winner:
                          s.winnerId === null
                            ? undefined
                            : s.winnerId === team.id,
                        inProgress: Boolean(s.startedAt) && !s.endedAt,
                        last: i === sets.length - 1,
                      })}
                    >
                      {s.status === "not_started" ||
                      (data.status === "completed" && s.status !== "completed")
                        ? "-"
                        : teamA?.id === team.id
                          ? score.a
                          : score.b}
                    </div>
                  );
                })}
              <Handle
                type="source"
                position={Position.Right}
                id={team.id.toString()}
                style={{
                  opacity: 0,
                  position: "absolute",
                  top: i === 0 ? "45%" : "78%",
                  right: 0,
                }}
              />
            </div>
          );
        })}
      </div>
      {isDefined(loserFinish) && (
        <div className="px-3 py-1 text-end">
          Losers finish {formatOrdinals(loserFinish)}
        </div>
      )}
      {switchSlot && (
        <SwitchTeamForm
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) setSwitchSlot(undefined);
          }}
          tournamentDivisionId={divisionId}
          matchId={data.id}
          slot={switchSlot.slot}
          currentTeamId={switchSlot.currentTeamId}
        />
      )}
    </div>
  );
}
